import { Notification, NotificationType, updateNotification } from '../../../store/Notifications';
import { Icon } from '../../Atoms/Icon';
import { Text, TextBlock } from '../../Atoms/Text';
import { InstallUpdate } from './Body/InstallUpdate';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { SupportCaseNotification } from './Body/SupportCase';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { CommentNotificationBody } from './Body/CommentNotificationBody';
import { InventoryAsset } from '../../../types';
import { Comment } from '../../../store/Comment';
import { followAccountLink } from '../../../store/accountScopedData';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { UpcomingExpiration } from './Body/UpcomingExpiration';
import { StatusChange } from './Body/StatusChange';
import { AccountMerge } from './Body/AccountMerge';
import { FileMigrationComplete } from './Body/FileMigrationComplete';
import { OrderStatusUpdate } from './Body/OrderStatusUpdate';

export enum NotificationFocus {
  COMMENT = 'comment'
}

const UNOPENED_ACCOUNT_MESSAGE = 'Could not open the account this notification belongs to.';

// The paths match Comment#page_path in upstack_api; its email link also names the account, which the bell sets itself.
const getCommentUrl = (notification: Notification) => {
  const comment = notification?.notification_message?.content?.entity as Comment;
  const focus = `focus=${NotificationFocus.COMMENT}&commentId=${comment?.id}`;
  switch (comment?.entity_type) {
    case 'Inventory':
    case 'OutsideInventory':
      return `/inventory?detailId=${comment.entity_id}&${focus}`;
    case 'Opportunity':
      return `/orders/${comment.entity_id}?${focus}`;
    default:
      return '/';
  }
};

export const NotificationItem = ({
  notification,
  handleClose
}: {
  notification: Notification;
  handleClose: () => void;
}) => {
  const navigate = useNavigate();
  const { setSnack } = useSnackbar();

  const accountId = notification?.account_id;
  const entityId = notification?.entity_id;
  const notifyConfig = {
    [NotificationType.INSTALL_UPDATE]: {
      component: <InstallUpdate notification={notification} />,
      titleField: 'address',
      link: `/inventory?detailId=${entityId}`
    },
    [NotificationType.CASE_OPEN]: {
      component: <SupportCaseNotification notification={notification} />,
      titleField: 'address',
      link: `/support-cases?detailId=${entityId}`
    },
    [NotificationType.CASE_WORKING]: {
      component: <SupportCaseNotification notification={notification} />,
      titleField: 'address',
      link: `/support-cases?detailId=${entityId}`
    },
    [NotificationType.CASE_CLOSED]: {
      component: <SupportCaseNotification notification={notification} />,
      titleField: 'address',
      link: `/support-cases?detailId=${entityId}`
    },
    [NotificationType.COMMENT_MENTION]: {
      component: (
        <CommentNotificationBody
          notification={notification}
          verb="mentioned you"
        />
      ),
      titleField: 'address',
      link: getCommentUrl(notification)
    },
    [NotificationType.COMMENT_CREATED]: {
      component: (
        <CommentNotificationBody
          notification={notification}
          verb="commented"
        />
      ),
      titleField: 'address',
      link: getCommentUrl(notification)
    },
    [NotificationType.UPCOMING_EXPIRATION_30]: {
      component: (
        <UpcomingExpiration
          notification={notification}
          days="30"
        />
      ),
      titleField: 'address',
      link: `/inventory?detailId=${entityId}`
    },
    [NotificationType.UPCOMING_EXPIRATION_60]: {
      component: (
        <UpcomingExpiration
          notification={notification}
          days="60"
        />
      ),
      titleField: 'address',
      link: `/inventory?detailId=${entityId}`
    },
    [NotificationType.UPCOMING_EXPIRATION_90]: {
      component: (
        <UpcomingExpiration
          notification={notification}
          days="90"
        />
      ),
      titleField: 'address',
      link: `/inventory?detailId=${entityId}`
    },
    [NotificationType.STATUS_CHANGE]: {
      component: <StatusChange notification={notification} />,
      titleField: 'address',
      link: notification.notification_message.content?.object_type?.includes('project')
        ? `/projects?detailId=${entityId}`
        : `/inventory?detailId=${entityId}`
    },
    [NotificationType.ACCOUNT_MERGE]: {
      component: <AccountMerge notification={notification} />,
      titleField: 'account_merge',
      link: null
    },
    [NotificationType.FILE_MIGRATION_COMPLETE]: {
      component: <FileMigrationComplete notification={notification} />,
      titleField: 'file_migration_complete',
      link: null
    },
    [NotificationType.ORDER_STATUS_UPDATE]: {
      component: <OrderStatusUpdate notification={notification} />,
      titleField: 'order',
      link: notification.notification_message.content?.workflow_tab
        ? `/orders?tab=${notification.notification_message.content.workflow_tab}`
        : '/orders'
    }
  };
  const notifyMeta =
    notifyConfig[notification?.notification_message?.message_type as keyof typeof notifyConfig] ||
    notifyConfig['Install Update'];

  const getTitleText = (notification: Notification, field = 'address') => {
    let value = '';
    const entity = notification?.notification_message?.content?.entity;
    switch (field) {
      case 'address':
        value = (entity as InventoryAsset)?.address || (entity as Comment)?.entity_name || 'Data missing';
        break;
      case 'account_merge':
        value = 'Account Name Update';
        break;
      case 'file_migration_complete':
        value = 'Asset File migration completed';
        break;
      case 'order': {
        const content = notification?.notification_message?.content;
        const isQuoteReview = content?.workflow_tab === 'reviewing_approving_quotes';
        const primary = isQuoteReview ? content?.request_name : content?.request_number;
        const secondary = isQuoteReview ? content?.request_number : content?.request_name;
        value = primary || secondary || 'Order update';
        break;
      }
      default:
        break;
    }
    return (
      <div
        className="ml-6"
        title={value}>
        <Text
          size="sm"
          color="grey5">
          {`${value?.length > 25 ? `${value?.substring(0, 25)}...` : value}`}
        </Text>
      </div>
    );
  };

  return (
    <li
      className="border-t-2"
      key={notification.id}
      style={{ display: 'grid', whiteSpace: 'normal' }}>
      <div className="px-4 py-3">
        <div className="flex grow w-full items-center">
          <Icon type={!notification.is_read ? 'notificationsDot' : 'notificationsReadDot'} />
          {getTitleText(notification, notifyMeta?.titleField)}
          <div className="flex ml-auto space-x-1">
            {notifyMeta?.link && (
              <IconButton
                data-cy="notification-entity"
                title="Navigate to entity"
                onClick={async () => {
                  handleClose();
                  // Switched first: the list routes send an empty selection home before a lookup could answer.
                  const outcome = await followAccountLink(accountId);
                  // Under the selection it could not switch, the record's page would open on an empty drawer.
                  if (accountId && outcome === 'unresolved') {
                    setSnack({ message: UNOPENED_ACCOUNT_MESSAGE, type: 'error', open: true });
                    return;
                  }
                  navigate(notifyMeta?.link);
                }}>
                <LinkIcon />
              </IconButton>
            )}
            <Icon
              title="Dismiss notification"
              className="p-1 hover:cursor-pointer"
              type="cancel"
              onClick={() =>
                updateNotification({
                  ...notification,
                  is_archived: true,
                  is_read: true
                })
              }
            />
          </div>
        </div>
        {notifyMeta.component}
        {notification.created_at && (
          <TextBlock
            className="ml-9 mt-2"
            size="sm"
            color="grey4">
            {formatDistanceToNow(parseISO(notification.created_at))} ago
          </TextBlock>
        )}
      </div>
    </li>
  );
};
